import { Button, Flex, Text, SimpleGrid, Checkbox } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { useFormik } from "formik";
import { FormWrapper } from "@/components/FormWrapper/FormWrapper";
import { BaseInput } from "@/components/Controls/BaseInput";
import { BaseSelect, SelectOption } from "@/components/Controls/BaseSelect";
import { mockPartners } from "@/pages/PartnersPage/mockData";
import { mockLeadContacts, LeadContact } from "@/pages/LeadsContactsPage/mockData";
import { useMemo, useEffect, useState } from "react";
import { LeadContactForm, leadsOptions } from "../CreateLeadContactPage";

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";

  if (digits.startsWith("7")) {
    const phoneDigits = digits.slice(1);
    if (phoneDigits.length === 0) return "+7 (";
    if (phoneDigits.length <= 3) return `+7 (${phoneDigits}`;
    if (phoneDigits.length <= 6) return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3)}`;
    if (phoneDigits.length <= 8) return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
    return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 8)}-${phoneDigits.slice(8, 10)}`;
  }

  if (digits.length <= 1) return `+7 (${digits}`;
  if (digits.length <= 4) return `+7 (${digits}`;
  if (digits.length <= 7) return `+7 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 9) return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`;
};

export const UpdateLeadContactPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentContact, setCurrentContact] = useState<LeadContact | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadContact = () => {
      setIsLoading(true);
      const contact = mockLeadContacts.find((c) => c.id === Number(id));
      if (contact) {
        setCurrentContact(contact);
      }
      setIsLoading(false);
    };
    if (id !== undefined) {
      loadContact();
    }
  }, [id]);

  const partnerOptions: SelectOption[] = useMemo(() => {
    return mockPartners.map((partner) => ({
      value: partner.id,
      label: partner.name,
    }));
  }, []);

  const formik = useFormik<LeadContactForm>({
    initialValues: {
      name: "",
      position: "",
      work_phone: "",
      work_email: "",
      telegram: "",
      social_network: "",
      partner_id: null,
      leads: [],
    },
    onSubmit: () => {
      console.log("Обновление контакта:", formik.values);
    },
  });

  const initialContactValues = useMemo((): LeadContactForm | null => {
    if (!currentContact) return null;

    return {
      name: currentContact.name ?? "",
      position: currentContact.position ?? "",
      work_phone: currentContact.work_phone ?? "",
      work_email: currentContact.work_email ?? "",
      telegram: "",
      social_network: "",
      partner_id: null,
      leads: [],
    };
  }, [currentContact]);

  const hasChanges = useMemo(() => {
    if (!initialContactValues) return false;

    const current = formik.values;
    const initial = initialContactValues;

    const fieldsToCheck = Object.keys(initial) as Array<keyof LeadContactForm>;

    return fieldsToCheck.some((field) => {
      const currentValue = current[field];
      const initialValue = initial[field];

      if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
        return JSON.stringify([...currentValue].sort()) !== JSON.stringify([...initialValue].sort());
      }

      return currentValue !== initialValue;
    });
  }, [formik.values, initialContactValues]);

  useEffect(() => {
    if (initialContactValues && !formik.dirty) {
      formik.resetForm({ values: initialContactValues });
    }
  }, [initialContactValues, formik.dirty]);

  const handleLeadChange = (lead: string, isChecked: boolean) => {
    if (isChecked) {
      formik.setFieldValue("leads", [...formik.values.leads, lead]);
    } else {
      formik.setFieldValue(
        "leads",
        formik.values.leads.filter((l) => l !== lead),
      );
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    formik.setFieldValue("work_phone", formatted);
  };

  if (isLoading) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text>Загрузка...</Text>
        </Flex>
      </Layout>
    );
  }

  if (!currentContact) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text>Контакт не найден</Text>
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
        <Text color="gray.500" cursor="pointer" onClick={() => navigate("/leads_contacts")} display="flex" alignItems="center" gap="8px">
          <ArrowBackIcon />
          Назад к списку
        </Text>
        <Flex flexDirection="column" gap="4px">
          <Text fontSize={30} lineHeight="36px" fontWeight={700} color="#3b515e">
            Редактирование контакта
          </Text>
          <Text fontSize={16} lineHeight="24px" fontWeight={400} color="#65758b">
            Измените информацию о контакте
          </Text>
        </Flex>

        <FormWrapper>
          <Flex flexDirection="column" gap="24px">
            <Text fontSize={18} fontWeight={600} color="#3b515e">
              Основная информация
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <BaseInput
                fieldTitle="Имя"
                requiredField={true}
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder="Введите имя"
                name="name"
              />

              <BaseInput
                fieldTitle="Должность"
                requiredField={true}
                value={formik.values.position}
                onChange={formik.handleChange}
                placeholder="Введите должность"
                name="position"
              />

              <BaseInput
                fieldTitle="Рабочий телефон"
                value={formik.values.work_phone}
                onChange={handlePhoneChange}
                placeholder="+7 (__) __-__-__"
                name="work_phone"
              />

              <BaseInput
                fieldTitle="Рабочая почта"
                value={formik.values.work_email}
                onChange={formik.handleChange}
                placeholder="email@example.com"
                name="work_email"
                type="email"
              />

              <BaseInput
                fieldTitle="Telegram"
                value={formik.values.telegram}
                onChange={formik.handleChange}
                placeholder="@username"
                name="telegram"
              />

              <BaseInput
                fieldTitle="Соц. сеть"
                value={formik.values.social_network}
                onChange={formik.handleChange}
                placeholder="https://linkedin.com/in/username"
                name="social_network"
              />
            </SimpleGrid>

            <BaseSelect
              fieldTitle="Партнер"
              options={partnerOptions}
              placeholder="Выберите партнера"
              value={formik.values.partner_id}
              onChange={(value) => formik.setFieldValue("partner_id", value)}
            />

            <Text fontSize={18} fontWeight={600} color="#3b515e">
              Лиды
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {leadsOptions.map((lead) => (
                <Checkbox
                  key={lead}
                  isChecked={formik.values.leads.includes(lead)}
                  onChange={(e) => handleLeadChange(lead, e.target.checked)}
                  borderColor="#AAB0C1"
                  colorScheme="blue"
                >
                  {lead}
                </Checkbox>
              ))}
            </SimpleGrid>
          </Flex>
        </FormWrapper>

        <Flex gap="16px" justifyContent="flex-end">
          <Button onClick={() => navigate("/leads_contacts")}>Отмена</Button>
          <Button
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
            isDisabled={!formik.isValid || !hasChanges || !formik.values.name || !formik.values.position}
            onClick={() => formik.handleSubmit()}
          >
            Сохранить изменения
          </Button>
        </Flex>
      </Flex>
    </Layout>
  );
};
